import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit } from '@fortawesome/free-solid-svg-icons';
import { withAppHOC } from '../../hoc';
import PageHeading from '../../components/PageHeader/PageHeader';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import NoData from '../../components/NoData/NoData';
import {
  Form,
  Fieldset,
  Label,
  TextInput,
  ResetButton,
  MessageBox,
  Button,
  CustomConfirmButton,
} from '../../components/FormElements';
import { TableBuilder } from '../../components/TableElements';
import FiltersButton from '../../components/FiltersButton/FiltersButton';
import Pagination from '../../components/Pagination/Pagination';
import { StyledLink, StyledFiltersContainer } from '../../components/Styled';
import API from '../../api';
import { API_RESPONSE_TYPES } from '../../constants';
import { getSearchParams } from '../../utils';

const PackagesRead = (props) => {
  const [paginationData, setPaginationData] = useState({
    pageNumber: getSearchParams('pageNumber') || 1,
    recordsPerPage: getSearchParams('recordsPerPage') || 10,
  });
  const [totalRecords, setTotalRecords] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [responseStatus, setResponseStatus] = useState('');
  const [responseMessage, setResponseMessage] = useState('');
  const [packageName, setPackageName] = useState('');
  const [packages, setPackages] = useState([]);

  const readData = useCallback(
    (packageName = '') => {
      setIsLoading(true);
      setResponseStatus('');
      setResponseMessage('');
      API.get(
        `/packages/?pageNumber=${paginationData.pageNumber}&recordsPerPage=${paginationData.recordsPerPage}&packageName=${packageName}`
      )
        .then((response) => {
          const { status, message, data, totalRecords } = response.data;
          if (status === API_RESPONSE_TYPES.FAILURE) {
            setResponseStatus(status);
            setResponseMessage(message);
          } else {
            setTotalRecords(totalRecords);
            setPackages(data);
          }
        })
        .catch((error) => {
          setResponseStatus(API_RESPONSE_TYPES.FAILURE);
          setResponseMessage(error.message);
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
    [paginationData]
  );

  const deleteData = (id) => {
    setIsLoading(true);
    setResponseStatus('');
    setResponseMessage('');
    API.delete(`/packages/${id}`)
      .then((response) => {
        const { status, message } = response.data;
        if (status === API_RESPONSE_TYPES.FAILURE) {
          setResponseStatus(status);
          setResponseMessage(message);
        } else {
          reloadData();
        }
      })
      .catch((error) => {
        setResponseStatus(API_RESPONSE_TYPES.FAILURE);
        setResponseMessage(error.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const reloadData = () => {
    readData(packageName);
  };

  const resetFilters = () => {
    setPackageName('');
    readData();
  };

  const filterData = (e) => {
    e.preventDefault();
    readData(packageName);
  };

  useEffect(() => {
    readData();
  }, [paginationData, readData]);

  const [showFilters, setShowFilters] = useState(false);
  const onClickShowFilters = () => {
    setShowFilters((showFilters) => !showFilters);
  };

  return (
    <>
      <PageHeading
        {...props}
        canCreate={true}
        createLink={'create'}
        createTitle='Create Package'
        canReload={true}
        onClickReload={reloadData}
      />
      <FiltersButton onClick={onClickShowFilters} value={showFilters} />
      <StyledFiltersContainer className={showFilters ? 'show' : ''}>
        <Form method='POST' action='#' onSubmit={filterData}>
          <Fieldset>
            <Label>Package Name</Label>
            <TextInput
              value={packageName}
              onChange={setPackageName}
              placeholder='Search package name'
              disabled={isLoading}
            />
          </Fieldset>
          <Fieldset>
            <Button disabled={isLoading} type='submit'>
              Filter
            </Button>
            <ResetButton
              disabled={isLoading}
              type='button'
              onClick={resetFilters}
            >
              Clear
            </ResetButton>
          </Fieldset>
        </Form>
      </StyledFiltersContainer>
      <LoadingSpinner
        isLoading={responseStatus === '' && packages.length === 0 && isLoading}
      />
      <NoData
        status={
          responseStatus !== API_RESPONSE_TYPES.FAILURE &&
          !isLoading &&
          packages.length === 0
        }
        message={`No packages found`}
      />
      {responseStatus === API_RESPONSE_TYPES.FAILURE && (
        <MessageBox status={responseStatus} message={responseMessage} />
      )}
      {packages.length > 0 && (
        <>
          <TableBuilder
            isLoading={packages.length !== 0 && isLoading}
            tableHeadings={[
              {
                title: '',
                dataSelector: 'id',
                align: 'center',
                sticky: true,
                canSort: false,
                width: '100px',
                CellRenderer: (value) => (
                  <>
                    <StyledLink to={`update/${value}`} title={'Update Package'}>
                      <FontAwesomeIcon icon={faEdit} />
                    </StyledLink>
                    <CustomConfirmButton onClick={() => deleteData(value)} />
                  </>
                ),
              },
              {
                title: 'Package Name',
                dataSelector: 'packageName',
                dataType: 'string',
              },
              {
                title: 'Duration',
                dataSelector: 'duration',
                dataType: 'number',
                CellRenderer: (value) => (value ? `${value} Days` : 'N/A'),
              },
            ]}
            tableData={packages}
          />
          <Pagination
            totalRecords={totalRecords}
            paginationData={paginationData}
            setPaginationData={setPaginationData}
          />
        </>
      )}
    </>
  );
};

export default withAppHOC(PackagesRead);
